import { StrictMode, useEffect, useState } from 'react';
import { createRoot } from 'react-dom/client';
import { api, type Me } from './api.ts';
import { useSession } from './auth-client.ts';
import { openAccountDb } from './local/db.ts';
import { startFolder } from './local/folder.ts';
import { IdleGuard, useSite } from './idle.tsx';
import { navigate, useLocation } from './router.tsx';
import { t } from './strings.ts';
import { adoptTheme, setThemeSaver } from './theme.tsx';
import { ContactPage, LegalPage, SiteFooter, TermsGate } from './legal.tsx';
import { ApprovePage, WaitingPage } from './pages/access.tsx';
import { AdminPortal, AdminSite } from './pages/admin.tsx';
import { AccountPage, CheckEmailPage, ForgotPage, ResetPage, SignInPage, SignUpPage } from './pages/auth.tsx';
import { HomePage } from './pages/home.tsx';
import { LandingPage } from './pages/landing.tsx';
import { EditorPage } from './pages/editor.tsx';
import { FoldPage } from './pages/fold.tsx';
import { EnvelopePage } from './pages/envelope.tsx';
import { MailboxPage } from './pages/mailbox.tsx';
import { PrintPage } from './pages/print.tsx';
import { PausedPage } from './paused.tsx';
import { RsvpPage } from './pages/rsvp.tsx';
import { ConfirmHost } from './confirm.tsx';
import { ContactHost } from './contact.tsx';
import './styles.css';

/**
 * The admin portal's own address (admin.foldmycard.com, or admin.localhost here)
 * shows only the portal, with its own sign-in (pages/admin.tsx).
 */
const ADMIN_SITE = location.hostname.startsWith('admin.');

const PUBLIC: Record<string, () => React.JSX.Element> = {
  '/signin': SignInPage,
  '/signup': SignUpPage,
  '/check-email': CheckEmailPage,
  '/forgot': ForgotPage,
  '/reset-password': ResetPage,
  '/dev/mailbox': MailboxPage,
  '/approve': ApprovePage,           // the approver's email link: the signed link is the key, not a session
  '/terms': () => <LegalPage which="terms" />,
  '/disclaimer': () => <LegalPage which="disclaimer" />,
  '/privacy': () => <LegalPage which="privacy" />,
  '/contact': ContactPage,
  // the front page, for the signed-in too: at "/" they have their cards (2026-10-11)
  '/welcome': LandingPage,
};

/**
 * The signed-in account: whether it may use the app yet, its appearance, and
 * whether it approves sign-ups. Its theme replaces whatever this device had,
 * and a change of theme is saved back to it from then on. Its cards and
 * pictures are in its own store on this device (local/db.ts), opened here -
 * and once the account may use the card designs, its card folder (local/folder.ts).
 */
function useAccount(userId: string | undefined) {
  const [me, setMe] = useState<Me | null>(null);
  const [asked, setAsked] = useState(0);
  useEffect(() => {
    setMe(null);
    openAccountDb(userId ?? null);
    if (!userId) { setThemeSaver(null); return; }
    let live = true;
    api.me().then((m) => {
      if (!live) return;
      setMe(m);
      adoptTheme(m.theme);
      setThemeSaver((c) => { void api.saveSettings(c).catch(() => {}); });
      if (m.terms.accepted && m.approval === 'approved') {
        void startFolder((id, paper, hash) => api.template(id, paper, hash).then(() => true, () => false));
      }
    }).catch(() => {
      // the API still enforces approval on every request; the page just opens without the account's details
      if (live) setMe({ id: userId, email: '', name: '', approval: 'approved', theme: 'dark', approver: false,
        terms: { version: '', effectiveDate: '', accepted: true, acceptedVersion: null, acceptedAt: null } });
    });
    return () => { live = false; };
  }, [userId, asked]);
  /** Load the account again - after it has accepted the Terms, say. */
  return { me, reload: () => setAsked((n) => n + 1) };
}

function App() {
  const loc = useLocation();
  if (ADMIN_SITE) return <AdminSite />;
  const page = <Page />;
  // the print page is the printed sheet: nothing else on it
  if (/^\/print\//.test(loc.pathname)) return page;
  return <>{page}<SiteFooter /><ContactHost /></>;
}

/** Go somewhere else entirely (another address): after render, never during it. */
function Leave({ to }: { to: string }) {
  useEffect(() => { location.replace(to); }, [to]);
  return <main className="page"><p className="muted">{t('loading')}</p></main>;
}

function Page() {
  const loc = useLocation();
  const { data: session, isPending, isRefetching } = useSession();
  const site = useSite();
  const { me, reload } = useAccount(session?.user.id);
  const Public = PUBLIC[loc.pathname];
  // what a guest's invitation code opens (pages/rsvp.tsx): public, no sign-in
  const rsvp = loc.pathname.match(/^\/r\/([A-Za-z0-9_-]{4,40})$/);
  // a session check still on its way is not "signed out" (that sent people back to sign in twice)
  const signedOut = !isPending && !isRefetching && !session;
  const print = loc.pathname.match(/^\/print\/([0-9a-f-]{36})$/);
  const adminPath = loc.pathname === '/admin' || loc.pathname.startsWith('/admin/');

  // the front page, for anyone not signed in (pages/landing.tsx)
  const front = loc.pathname === '/' && signedOut;

  // anything but the public pages needs a signed-in (and so verified) user
  useEffect(() => {
    if (!Public && !rsvp && !front && signedOut) {
      navigate(`/signin?next=${encodeURIComponent(loc.pathname + loc.search)}`, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [Public, !!rsvp, front, signedOut, loc.pathname, loc.search]);

  // paused from the admin portal: a note for everyone (the portal at /admin keeps working)
  if (site?.paused && !adminPath) return <PausedPage message={site.pausedMessage} />;
  if (rsvp) return <RsvpPage token={rsvp[1]} />;
  if (Public) return <Public />;
  if (front) return <LandingPage />;
  if (isPending || !session || !me) return <main className="page"><p className="muted">{t('loading')}</p></main>;

  const admin = adminPath && me.approver;
  const idle = <IdleGuard minutes={(admin ? site?.adminIdleMinutes : site?.idleMinutes) ?? 30} since={session.session.createdAt} />;
  return <>{idle}<ConfirmHost />{signedIn()}</>;

  function signedIn() {
    // before anything else, the Terms (legal.tsx); until approved, a note that says why (approval.ts) -
    // the account page stays reachable throughout, so an account can always be deleted
    if (loc.pathname === '/account') return <AccountPage />;
    if (!me!.terms.accepted) return <TermsGate me={me!} onAccepted={reload} />;
    if (me!.approval !== 'approved') return <WaitingPage email={me!.email} approval={me!.approval} onApproved={reload} />;
    if (admin) {
      // the portal has an address of its own: go there; otherwise it is here, at /admin
      if (!site) return <main className="page"><p className="muted">{t('loading')}</p></main>;
      if (site.adminUrl) return <Leave to={`${site.adminUrl}${loc.pathname.slice('/admin'.length) || '/'}${loc.search}`} />;
      return <AdminPortal base="/admin" />;
    }

    if (print) return <PrintPage key={print[1]} projectId={print[1]} />;
    const project = loc.pathname.match(/^\/projects\/([0-9a-f-]{36})$/);
    if (project) return <EditorPage key={project[1]} projectId={project[1]} />;
    const fold = loc.pathname.match(/^\/projects\/([0-9a-f-]{36})\/fold$/);
    if (fold) return <FoldPage key={fold[1]} projectId={fold[1]} />;
    const envelope = loc.pathname.match(/^\/projects\/([0-9a-f-]{36})\/envelope$/);
    if (envelope) return <EnvelopePage key={envelope[1]} projectId={envelope[1]} />;
    return <HomePage email={session!.user.email} name={me!.name} approver={me!.approver} />;
  }
}

createRoot(document.getElementById('root')!).render(<StrictMode><App /></StrictMode>);
